import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileContract, faShieldAlt, faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';
import { faFacebook as faFacebookBrand, faTwitter as faTwitterBrand, faLinkedin as faLinkedinBrand, faGithub as faGithubBrand } from '@fortawesome/free-brands-svg-icons';
import BrandLogo from './BrandLogo';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="gp-footer mt-auto" role="contentinfo">
      <div className="gp-container py-8 sm:py-9">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
          <div className="space-y-3">
            <BrandLogo to="/" size="sm" />
            <p className="text-xs leading-relaxed opacity-80 max-w-xs">
              {t('footer.description')}
            </p>
            <div className="flex gap-3">
              {[
                { href: 'https://facebook.com', icon: faFacebookBrand, label: 'Facebook' },
                { href: 'https://twitter.com', icon: faTwitterBrand, label: 'Twitter' },
                { href: 'https://linkedin.com', icon: faLinkedinBrand, label: 'LinkedIn' },
                { href: 'https://github.com', icon: faGithubBrand, label: 'GitHub' },
              ].map(({ href, icon, label }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} className="opacity-60 hover:opacity-100 transition-opacity">
                  <FontAwesomeIcon icon={icon} className="text-sm" />
                </a>
              ))}
            </div>
          </div>

          <nav aria-label={t('footer.quickLinks')}>
            <h3 className="text-xs font-bold uppercase tracking-wider mb-3 opacity-90">
              {t('footer.quickLinks')}
            </h3>
            <ul className="space-y-1.5 text-sm">
              {[
                { to: '/', label: t('nav.home') },
                { to: '/learn', label: t('nav.learn') },
                { to: '/certifications', label: t('nav.certifications') },
                { to: '/pricing', label: t('nav.pricing') },
                { to: '/dashboard', label: t('nav.dashboard') },
              ].map(({ to, label }) => (
                <li key={to}>
                  <Link to={to} className="hover:underline">{label}</Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label={t('footer.legalSupport')}>
            <h3 className="text-xs font-bold uppercase tracking-wider mb-3 opacity-90">
              {t('footer.legalSupport')}
            </h3>
            <ul className="space-y-1.5 text-sm">
              {[
                { to: '/terms', icon: faFileContract, label: t('footer.termsOfService') },
                { to: '/privacy', icon: faShieldAlt, label: t('footer.privacyPolicy') },
                { to: '/contact', icon: faQuestionCircle, label: t('footer.contactUs') },
                { to: '/faq', icon: faQuestionCircle, label: t('footer.faq') },
              ].map(({ to, icon, label }) => (
                <li key={to}>
                  <Link to={to} className="inline-flex items-center gap-1.5 hover:underline">
                    <FontAwesomeIcon icon={icon} className="text-[10px] opacity-70" aria-hidden="true" />
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="mt-6 pt-5 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-3">
          <p className="text-xs opacity-70">{t('footer.copyright', { year: currentYear })}</p>
        </div>

        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="text-[11px] opacity-50 text-center leading-relaxed max-w-4xl mx-auto">
            We partner with Microsoft Clarity and Microsoft Advertising to capture how you use and interact with our website through behavioral metrics, heatmaps, and session replay to improve and market our products/services. Website usage data is captured using first and third-party cookies and other tracking technologies to determine the popularity of products/services and online activity. Additionally, we use this information for site optimization, fraud/security purposes, and advertising. For more information about how Microsoft collects and uses your data, visit the{' '}
            <a
              href="https://privacy.microsoft.com/privacystatement"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:opacity-80"
            >
              Microsoft Privacy Statement
            </a>
            .
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
